import { useRef, useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import {
  FormControl,
  FormHelperText,
  InputBase,
  InputLabel,
  InputBaseProps,
  Typography,
  useMediaQuery,
} from '@mui/material';
// import { InputBase } from '@mui/material/TextField';
import { useField, useFormikContext } from 'formik';
import { values } from 'lodash';
import { Box } from '@mui/system';
import InfoIconButton from '../../components/InfoIconButton';

export const BootstrapInput = styled(InputBase)(({ theme }) => ({
  'label + &': {
    marginTop: theme.spacing(4),
  },
  '&.MuiInputBase-root': {
    borderRadius: 4,
    position: 'relative',
    backgroundColor: theme.palette.common.white,
    border: '1px solid #5E9BC6',
    fontSize: 16,
    // transition: theme.transitions.create(['border-color', 'background-color', 'box-shadow']),
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
    '.MuiInputBase-input': {
      margin: '5px 12px',
      borderRadius: 4,
      minWidth: 0,
      flex: 1,
    },
    '@media (max-width: 768px)': {
      width: '100%',
      marginLeft: 0,
      '.MuiInputBase-input': {
        margin: '5px 6px',
        fontSize: 14,
      },
    },
    '@media (max-width: 320px)': {
      width: '100%',
      maxWidth: '100%',
      marginLeft: 0,
      '.MuiInputBase-input': {
        margin: '4px 2px',
        fontSize: 'clamp(10px, 3.5vw, 13px)',
        padding: '0 2px',
        '&::placeholder': {
          fontSize: 10,
        },
      },
    },
    '&.mf-display-only': {
      '@media (max-width: 768px)': {
        backgroundColor: 'rgba(0, 0, 0, 0.06)',
        '& .MuiInputBase-input': {
          color: 'rgba(0, 0, 0, 0.38)',
          WebkitTextFillColor: 'rgba(0, 0, 0, 0.38)',
          cursor: 'text',
          overflowX: 'auto',
          textOverflow: 'clip',
        },
      },
    },
    '&.Mui-disabled .MuiInputBase-input': {
      textOverflow: 'clip',
      '@media (max-width: 768px)': {
        overflowX: 'auto',
      },
    },
    '&.MuiInputBase-adornedStart': {
      '@media (max-width: 1280px)': {
        '.MuiInputBase-input': {
          margin: '5px 4px',
          textOverflow: 'clip',
          '&::placeholder': {
            fontSize: 13,
          },
        },
        '.MuiInputAdornment-root': {
          marginLeft: '6px',
          paddingRight: '6px',
          '.MuiOutlinedInput-root .MuiSelect-select': {
            padding: '0 20px 0 0',
            minWidth: '2em',
          },
        },
      },
      '@media (max-width: 768px)': {
        '.MuiInputBase-input': {
          margin: '5px 4px',
          '&::placeholder': {
            fontSize: 12,
          },
        },
        '.MuiInputAdornment-root': {
          marginLeft: '6px',
          paddingRight: '6px',
          '.MuiOutlinedInput-root .MuiSelect-select': {
            padding: '0 22px 0 0',
          },
        },
      },
      '@media (max-width: 480px)': {
        '.MuiInputBase-input': {
          margin: '5px 3px',
          '&::placeholder': {
            fontSize: 11,
          },
        },
        '.MuiInputAdornment-root': {
          marginLeft: '4px',
          paddingRight: '4px',
        },
      },
    },
    '.MuiInputAdornment-root': {
      marginLeft: '12px',
      marginRight: 0,
      paddingRight: '12px',
      borderRight: '1px solid #5E9BC6',
      height: '45px',
      flexShrink: 0,
      '.MuiCardMedia-root': {
        width: 24,
      },
      '.MuiOutlinedInput-root': {
        '& .MuiSelect-select': {
          padding: '0 24px 0 0',
          minWidth: '2.5em',
          whiteSpace: 'nowrap',
        },
        '.MuiSelect-icon': {
          right: 0,
        },
        '&.Mui-focusVisible, &:has(.MuiSelect-select:focus-visible)': {
          outline: '2px solid #153760',
          outlineOffset: '2px',
          borderRadius: '4px',
        },
      },
    },
    '& input::placeholder': {
      textTransform: 'none',
      color: '#72777C',
      opacity: 1,
    },
    '&.Mui-disabled input::placeholder': {
      color: '#72777C',
      opacity: 1,
      WebkitTextFillColor: '#72777C',
    },
    'input::-webkit-outer-spin-button, input::-webkit-inner-spin-button': {
      WebkitAppearance: 'none',
      margin: 0,
    },
    'input[type=number]': {
      MozAppearance: 'textfield',
    },
  },
}));

export default function MFTextField({
  name,
  label,
  placeholder,
  defaultValue,
  readonly,
  inputProps,
  margin,
  startAdornment,
  onChange,
  sx,
  trimOnBlur = true,
  displayCharecterCounter = '',
  applyMarginBottom = true,
  onInfoClick,
  infoAriaLabel,
  infoStyle,
  regexForFilterValue,
  required = false,
  enableStatusMessage = false,
  ...props
}: InputBaseProps & {
  name: string;
  label?: string;
  margin?: 'dense' | 'none' | undefined;
  placeholder: string;
  defaultValue?: string;
  readonly?: boolean;
  startAdornment?: JSX.Element;
  trimOnBlur?: boolean;
  displayCharecterCounter?: string;
  applyMarginBottom?: boolean;
  onInfoClick?: any;
  infoAriaLabel?: string;
  infoStyle?: any;
  regexForFilterValue?: RegExp;
  required?: boolean;
  enableStatusMessage?: boolean;
}): JSX.Element {
  const { disabled: disableInput = false, onKeyDown: onKeyDownProp, ...restProps } = props;
  const isDisplayOnly = disableInput || Boolean(readonly);
  const isNarrowViewport = useMediaQuery('(max-width: 768px)');
  const useReadOnlyDisplay = isDisplayOnly && isNarrowViewport;
  const id = `${name}-input`;
  const helperTextId = `${name}-error-text`;
  const statusId = `${name}-status`;
  const inputRef = useRef<HTMLInputElement>(null);
  const [field, meta, helpers] = useField(name);
  const { setFieldValue } = useFormikContext();
  const { setTouched } = helpers;
  const errorText = meta.error && meta.touched ? meta.error : '';
  const describedBy = errorText ? helperTextId : props['aria-describedby'];
  const mergedInputProps = {
    ...(inputProps || {}),
    'aria-describedby': describedBy,
    'aria-invalid': Boolean(errorText),
    ...(isDisplayOnly && field.value ? { title: String(field.value) } : {}),
    ...(isDisplayOnly && label && !inputProps?.['aria-label'] && !inputProps?.['aria-labelledby']
      ? { 'aria-label': label }
      : {}),
    'aria-required': required,
  };
  const [statusMessage, setStatusMessage] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { target } = e;
    const { value } = target;
    if (regexForFilterValue && value.match(regexForFilterValue)) {
      target.value = value.replace(regexForFilterValue as RegExp, '');
      field.onChange(e);
    } else {
      field.onChange(e);
    }
  };

  onChange = onChange || (regexForFilterValue ? handleChange : field.onChange);
  // eslint-disable-next-line
  const onBlur = (e: React.ChangeEvent<any>): void => {
    const { target: fieldTarget } = e;
    const { value } = fieldTarget;

    setTimeout(() => {
      trimOnBlur &&
        setFieldValue(
          name,
          value && typeof value === 'string'
            ? !meta.initialValue && value.trim() === ''
              ? meta.initialValue
              : value.trim()
            : value
        );
      setTouched(true, true);
    }, 0);
  };

  useEffect(() => {
    if (!enableStatusMessage) return;
    if (meta.touched && meta.error) {
      setStatusMessage(meta.error);
    } else {
      setStatusMessage('');
    }
  }, [enableStatusMessage, meta.error, meta.touched]);

  useEffect(() => {
    inputRef.current?.addEventListener('wheel', (e) => e.preventDefault());
  }, []);

  return (
    <FormControl
      variant="outlined"
      fullWidth
      sx={{
        my: { xs: 1, sm: 1, md: 2 },
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
      }}>
      {label && onInfoClick ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            flexWrap: 'wrap',
            maxWidth: '100%',
          }}>
          <InputLabel
            shrink
            htmlFor={id}
            required={required}
            sx={{
              transform: 'unset',
              fontSize: 14,
              fontWeight: 500,
              color: 'rgba(0,0,0,0.7)',
              position: 'static',
            }}>
            {label}
          </InputLabel>
          <InfoIconButton
            aria-label={infoAriaLabel || `More information about ${label}`}
            onClick={onInfoClick}
            sx={{
              flexShrink: 0,
              lineHeight: 1,
              ...infoStyle,
            }}
          />
        </Box>
      ) : (
        label && (
          <InputLabel
            shrink
            htmlFor={id}
            required={required}
            sx={{
              transform: 'unset',
              fontSize: 14,
              fontWeight: 500,
              color: 'rgba(0,0,0,0.7)',
            }}>
            {label}
          </InputLabel>
        )
      )}
      <BootstrapInput
        defaultValue={defaultValue}
        placeholder={placeholder}
        id={id}
        className={useReadOnlyDisplay ? 'mf-display-only' : undefined}
        startAdornment={startAdornment}
        {...field}
        onBlur={onBlur}
        margin={margin}
        disabled={isDisplayOnly && !useReadOnlyDisplay}
        required={required}
        readOnly={useReadOnlyDisplay}
        inputProps={{
          ...mergedInputProps,
          readOnly: useReadOnlyDisplay,
          'aria-disabled': isDisplayOnly ? true : undefined,
        }}
        {...restProps}
        onChange={useReadOnlyDisplay ? undefined : onChange}
        onKeyDown={useReadOnlyDisplay ? undefined : onKeyDownProp}
        ref={inputRef}
        sx={{
          ...(label && onInfoClick ? { mt: 4 } : {}),
          ...sx,
          ...(useReadOnlyDisplay && {
            pointerEvents: 'auto',
            '& .MuiInputBase-input': {
              pointerEvents: 'auto',
            },
          }),
        }}
      />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexDirection: errorText ? 'row' : 'row-reverse',
        }}>
        {errorText && (
          <FormHelperText id={helperTextId} error sx={{ marginLeft: 'unset' }}>
            {errorText}
          </FormHelperText>
        )}
        {displayCharecterCounter && (
          <FormHelperText sx={{ marginLeft: 'unset' }}>
            {`${field.value.toString().length} / ${displayCharecterCounter}`}
          </FormHelperText>
        )}
      </Box>
      {enableStatusMessage && (
        <Box
          id={statusId}
          role="status"
          aria-atomic="true"
          sx={{
            position: 'absolute',
            width: '1px',
            height: '1px',
            padding: 0,
            margin: '-1px',
            overflow: 'hidden',
            clip: 'rect(0, 0, 0, 0)',
            whiteSpace: 'nowrap',
            border: 0,
          }}>
          {statusMessage}
        </Box>
      )}
    </FormControl>
  );
}
